import DotsFessAgreement from "components/Agreements/DotsFessAgreement";
import DiscountConfigModal from 'components/Modal/DiscountConfigModal';
import DiscountConfigForm from 'components/Forms/DiscountConfigForm';
import ApiContext from "context/ApiContext";
import AuthContext from "context/AuthContext";
import { Field, Form, Formik } from "formik";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import logger from "utils/logger";
import * as Yup from "yup";
import InfoText from "./InfoTextComponent";
import { getRewardTypeLabel, HOST_REWARD_TYPES } from 'components/Forms/Multistep/constants';
import useCan from "hooks/useCan";
import { CAPABILITIES } from "utils/capabilities";
import { Modal } from "react-bootstrap";
import CommissionCalculator from "components/Dots/CommissionCalculator";



const normalizeRewardType = (value) => {
  if (!value || typeof value !== "string") {
    return HOST_REWARD_TYPES.CASH.VALUE;
  }
  const formattedValue = value.toLowerCase().replace(/\s+/g, "_");
  if (formattedValue === HOST_REWARD_TYPES.STORE_CREDIT.VALUE) {
    return HOST_REWARD_TYPES.STORE_CREDIT.VALUE;
  }
  if (formattedValue === HOST_REWARD_TYPES.GIFT_CARD.VALUE) {
    return HOST_REWARD_TYPES.GIFT_CARD.VALUE;
  }
  return HOST_REWARD_TYPES.CASH.VALUE;
};


/**
 * Gets the Bootstrap badge className for a reward type.
 * Returns different badge colors based on the reward type.
 *
 * @param {string} rewardType - The reward type value (e.g., "cash", "store_credit", "gift_card")
 * @returns {string} Bootstrap badge className ("bg-success" for Store Credit, "bg-primary" for Cash, "bg-warning" for Gift Card)
 * @example
 * getRewardTypeBadgeClassName('store_credit') // returns 'bg-success'
 * getRewardTypeBadgeClassName('cash') // returns 'bg-primary'
 * getRewardTypeBadgeClassName('gift_card') // returns 'bg-warning'
 */
const getRewardTypeBadgeClassName = rewardType => {
  if (rewardType === HOST_REWARD_TYPES.STORE_CREDIT.VALUE) {
    return 'bg-success';
  }
  if (rewardType === HOST_REWARD_TYPES.CASH.VALUE) {
    return 'bg-primary';
  }
  if (rewardType === HOST_REWARD_TYPES.GIFT_CARD.VALUE) {
    return 'bg-warning';
  }
  return 'bg-primary';
};

const StoreUserProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const { userProfile, updateStoreUserProfile } = useContext(AuthContext);
  const { can } = useCan();
  const canManageCommission = can(CAPABILITIES.COMMISSION_MANAGE);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [showCommissionCalculator, setShowCommissionCalculator] = useState(false);

  const handleSaveDiscountConfig = async discountConfig => {
    setDiscountLoading(true);
    try {
      // Include discount and discount_type in discount_config from store values
      const discountConfigWithValues = {
        ...discountConfig,
        discount: userProfile?.store?.discount_given || null,
        discount_type: userProfile?.store?.discount_type || null
      };

      // Build the same payload structure as the Edit flow (camelCase field names)
      const updateData = {
        phone_number: userProfile?.phone_number,
        about_me: userProfile?.about_me,
        instagram_handle: userProfile?.user_social?.instagram_handle,
        twitter_handle: userProfile?.user_social?.twitter_handle,
        tiktok_handle: userProfile?.user_social?.tiktok_handle,
        facebook_handle: userProfile?.user_social?.facebook_handle,
        latitude: userProfile?.user_geo_location?.latitude,
        longitude: userProfile?.user_geo_location?.longitude,
        address: userProfile?.user_geo_location?.address,
        street_number: userProfile?.user_geo_location?.street_number,
        city: userProfile?.user_geo_location?.city,
        state: userProfile?.user_geo_location?.state,
        zip_code: userProfile?.user_geo_location?.zip_code,
        profile_picture_id: userProfile?.profile_picture?.id,
        store_email: userProfile?.user_shopify?.[0]?.store_email,
        rewardType: normalizeRewardType(userProfile?.store?.host_reward_type),
        demoCommission: userProfile?.store?.demo_commission,
        commissionType: userProfile?.store?.demo_commission_type,
        salesCommission: userProfile?.store?.demo_sales_commission,
        salesCommissionType: userProfile?.store?.demo_sales_commission_type,
        no_show_demo_commission_type:
          userProfile?.store?.no_show_demo_commission_type,
        no_show_demo_commission: userProfile?.store?.no_show_demo_commission,
        discountType: userProfile?.store?.discount_type,
        discountGiven: userProfile?.store?.discount_given,
        paymentFrequency: userProfile?.store?.payment_frequency,
        timeEligibility: userProfile?.store?.sales_commission_order_threshold,
        is_guest_discount_active: userProfile?.store?.is_guest_discount_active ?? false,
        discount_config: discountConfigWithValues
      };
      await updateStoreUserProfile(updateData);
      setShowDiscountModal(false);
    } catch (error) {
      logger.error(`Error saving discount config: ${error}`);
    } finally {
      setDiscountLoading(false);
    }
  };

  return (
    <>
      <DotsFessAgreement />
      {/* ── Two-column layout: Commission Info (left) + Calculator (right) ── */}
      <div className="row g-3 mt-0 align-items-stretch">

        {/* Left column — Store Commission Info */}
        <div className="col-12 col-xl-5">
          <div className="card cardc store_commission_card h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0 ">Store Commission Info</h5>
              </div>
              <hr />
              {isEditing && canManageCommission ? (
                <EditingView setIsEditing={setIsEditing} />
              ) : (
                <CommissionView
                  setIsEditing={setIsEditing}
                  setShowDiscountModal={setShowDiscountModal}
                  canManageCommission={canManageCommission}
                />
              )}
            </div>
          </div>
        </div>

        {/* Right column — Commission Calculator */}
        <div className="col-12 col-xl-7">
          <div className="card cardc store_commission_card h-100">
            <div className="card-body p-0" style={{ overflow: "hidden", borderRadius: "inherit" }}>
              <StoreDetailView />
            </div>
          </div>
        </div>

      </div>

      {/* Discount Configuration Modal */}
      {userProfile?.store && canManageCommission && (
        <DiscountConfigModal
          show={showDiscountModal}
          onHide={() => setShowDiscountModal(false)}
          discountConfig={userProfile?.store?.discount_config || {}}
          onSave={handleSaveDiscountConfig}
          loading={discountLoading}
        />
      )}
    </>
  );
};

export default StoreUserProfile;

const StoreDetailView = () => {
  const { getDataShopifyShop } = useContext(ApiContext);
  const { userProfile, updateShopifyStoreDetail, user } =
    useContext(AuthContext);
  const [shopData, setShopData] = useState();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await updateShopifyStoreDetail(values);
      setLoading(false);
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    const response = async () => {
      setLoading(true);
      try {
        let res = await getDataShopifyShop(userProfile?.store?.store_id);
        setShopData(res?.data?.data[0]);
        setLoading(false);
      } catch (error) {
        console.log(error);
      }
    };
    response();
  }, []);

  if (loading) {
    return (
      <div className="spinner-dots p-4">
        <Spinner className="custom-blink" animation="grow" />
        <Spinner className="custom-blink" animation="grow" />
        <Spinner className="custom-blink" animation="grow" />
      </div>
    );
  }

  return <CommissionCalculator />;
};

const CommissionView = (props) => {
  let { userProfile } = useContext(AuthContext);
  const { setIsEditing, setShowDiscountModal, canManageCommission } = props;
  const rewardType = normalizeRewardType(userProfile?.store?.host_reward_type);

  return (
    <>
      <div className="row mb-3 align-items-center">
        <div className="col col-sm-6 col-md-6 commission_setting_view_col">
          <h6 className="mb-0">
            <div className="commission_text">Real Customer Reward Type</div>
            <InfoText text="Choose how hosts are rewarded for their meetings." />
          </h6>
        </div>
        <div className="col col-sm-6 col-md-6 commission_info_percentage_text">
          <span
            className={`badge ${getRewardTypeBadgeClassName(rewardType)}`}
          >
            {getRewardTypeLabel(rewardType)}
          </span>
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col col-sm-6 col-md-6 commission_setting_view_col">
          <h6 className="mb-0">
            <div className="commission_text">Video meeting Commission</div>
            <InfoText text="How much real customers will be paid for each meeting they are in." />
          </h6>
        </div>
        <div className="col col-sm-6 col-md-6 commission_info_percentage_text">
          {userProfile?.store?.demo_commission_type === "PERCENTAGE" ? (
            <>{userProfile?.store?.demo_commission}% </>
          ) : (
            <> ${userProfile?.store?.demo_commission}</>
          )}
        </div>
      </div>

      <hr />
      <div className="row">
        <div className="col col-sm-6 col-md-6 commission_setting_view_col">
          <h6 className="mb-0">
            <div className="commission_text">Sales Commission</div>
            <InfoText text="How much hosts will be paid on sales that their meetings generate." />
          </h6>
        </div>
        <div className="col col-sm-6 col-md-6 commission_info_percentage_text">
          {userProfile?.store?.demo_sales_commission_type === "PERCENTAGE" ? (
            <>{userProfile?.store?.demo_sales_commission}% </>
          ) : (
            <>${userProfile?.store?.demo_sales_commission} </>
          )}
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col col-sm-6 col-md-6 commission_setting_view_col">
          <h6 className="mb-0">
            <div className="commission_text">No Show Commission</div>
            <InfoText text="How much real customers will be paid for each meeting they are in, but no guest shows up." />
          </h6>
        </div>
        <div className="col col-sm-6 col-md-6 commission_info_percentage_text">
          {userProfile?.store?.no_show_demo_commission_type === "PERCENTAGE" ? (
            <> {userProfile?.store?.no_show_demo_commission}%</>
          ) : (
            <>${userProfile?.store?.no_show_demo_commission}</>
          )}
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col col-sm-6 col-md-6 commission_setting_view_col">
          <h6>
            <div className="commission_text d-flex align-items-center" style={{ gap: '0.5rem', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
              <span style={{ whiteSpace: 'nowrap' }}>Discount Amount</span>
              {userProfile?.store?.is_guest_discount_active ? (
                <span className="badge bg-success" style={{ fontSize: '0.75rem', flexShrink: 0 }}>Enabled</span>
              ) : (
                <span className="badge bg-secondary" style={{ fontSize: '0.75rem', flexShrink: 0 }}>Disabled</span>
              )}
            </div>
            <InfoText text="How much of a discount guests receive if they buy after their meeting." />
            {canManageCommission && (
              <button
                className="discount-config-link"
                onClick={() => setShowDiscountModal(true)}
                style={{
                  marginTop: '8px',
                  background: 'none',
                  border: 'none',
                  color: '#1890ff',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: '0',
                  fontSize: '0.875rem',
                  fontWeight: 'normal',
                  display: 'block'
                }}
              >
                Configure options
              </button>
            )}
          </h6>
        </div>
        <div className="col col-sm-6 col-md-6 commission_info_percentage_text">
          {userProfile?.store?.discount_type === "PERCENTAGE" ? (
            <> {userProfile?.store?.discount_given}%</>
          ) : (
            <>${userProfile?.store?.discount_given}</>
          )}
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col col-sm-6 col-md-8 commission_setting_view_col">
          <h6>
            <div className="commission_text">
              Sales Commission Time Eligibility
            </div>
            <InfoText text="Number of days after a meeting that the customer must buy for the Real Customer to be eligible for Sales Commission payment." />
          </h6>
        </div>
        <div className="col col-sm-6 col-md-4 commission_info_percentage_text">
          <>{userProfile?.store?.sales_commission_order_threshold}</>
        </div>
      </div>
      <hr />
      <div className="row">
        <div className="col-sm-12">
          <button
            className="add_email_button"
            onClick={() => setIsEditing(true)}
            hidden={!canManageCommission}
          >
            Edit
          </button>
        </div>
      </div>
    </>
  );
};

const EditingView = (props) => {
  const {
    userProfile,
    updateStoreUserProfile,
    getStoreRewardType,
    updateStoreRewardType,
  } = useContext(AuthContext);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rewardTypeLoading, setRewardTypeLoading] = useState(false);
  const [rewardTypeSaving, setRewardTypeSaving] = useState(false);
  const [rewardTypeError, setRewardTypeError] = useState("");
  const [initialRewardType, setInitialRewardType] = useState(
    normalizeRewardType(userProfile?.store?.host_reward_type)
  );
  const hasHydratedRewardType = useRef(false);
  const [discountEnabled, setDiscountEnabled] = useState(
    userProfile?.store?.is_guest_discount_active ?? true
  );
  const [discountConfig, setDiscountConfig] = useState(
    userProfile?.store?.discount_config || {}
  );
  const discountConfigFormRef = useRef(null);
  let { setIsEditing } = props;

  // Sync discountEnabled state when userProfile changes
  useEffect(() => {
    // Only update if the value is explicitly set (not undefined/null)
    if (userProfile?.store?.is_guest_discount_active !== undefined) {
      setDiscountEnabled(userProfile.store.is_guest_discount_active);
    }
    // If undefined, keep the default (true) from useState
  }, [userProfile?.store?.is_guest_discount_active]);

  // Sync discountConfig when userProfile changes
  useEffect(() => {
    if (userProfile?.store?.discount_config) {
      setDiscountConfig(userProfile?.store?.discount_config);
    }
  }, [userProfile?.store?.discount_config]);

  useEffect(() => {
    let isMounted = true;

    const fetchRewardType = async () => {
      // Only fetch if user has a store
      if (!userProfile?.store) {
        const fallback = normalizeRewardType(
          userProfile?.store?.host_reward_type
        );
        if (isMounted && !hasHydratedRewardType.current) {
          setInitialRewardType(fallback);
          hasHydratedRewardType.current = true;
        }
        return;
      }

      setRewardTypeLoading(true);
      try {
        const response = await getStoreRewardType();
        const hostRewardType =
          response?.data?.host_reward_type ||
          response?.data?.data?.host_reward_type;
        const normalized = normalizeRewardType(
          hostRewardType ?? userProfile?.store?.host_reward_type
        );
        if (isMounted && !hasHydratedRewardType.current) {
          setInitialRewardType(normalized);
          hasHydratedRewardType.current = true;
        }
        if (isMounted) {
          setRewardTypeError("");
        }
      } catch (fetchError) {
        logger.error(
          "[StoreUserProfile] getStoreRewardType failed",
          fetchError
        );
        // Handle 404 specifically - user might not have StoreAdmins record
        if (fetchError?.response?.status === 404) {
          // Fall back to using host_reward_type from userProfile
          const fallback = normalizeRewardType(
            userProfile?.store?.host_reward_type
          );
          if (isMounted && !hasHydratedRewardType.current) {
            setInitialRewardType(fallback);
            hasHydratedRewardType.current = true;
          }
          if (isMounted) {
            setRewardTypeError("");
          }
        } else {
          if (isMounted && !hasHydratedRewardType.current) {
            const fallback = normalizeRewardType(
              userProfile?.store?.host_reward_type
            );
            setInitialRewardType(fallback);
            hasHydratedRewardType.current = true;
          }
          if (isMounted) {
            setRewardTypeError(
              "Unable to load reward preference. Defaults to cash."
            );
          }
        }
      } finally {
        if (isMounted) {
          setRewardTypeLoading(false);
        }
      }
    };

    fetchRewardType();

    return () => {
      isMounted = false;
    };
  }, [
    getStoreRewardType,
    userProfile?.store?.host_reward_type,
    userProfile?.store,
  ]);

  const validationSchema = Yup.object().shape({
    timeEligibility: Yup.number()
      .typeError("Must be a number")
      .integer("Must be an integer")
      .min(1, "Value must be at least 1")
      .required("This field is required"),
  });

  const handleSubmit = (values) => {
    setSubmitting(true);
    setError(false);

    // Always get discount config from the form, regardless of enabled/disabled status
    let finalDiscountConfig = null;
    if (discountConfigFormRef.current && discountConfigFormRef.current.getValues) {
      try {
        finalDiscountConfig = discountConfigFormRef.current.getValues();
        // Include discount and discount_type from form values
        finalDiscountConfig.discount = values.discountGiven || null;
        finalDiscountConfig.discount_type = values.discountType || null;
      } catch (error) {
        logger.error('Error getting discount config values:', error);
        // Fallback to stored config
        finalDiscountConfig = {
          ...discountConfig,
          discount: values.discountGiven || null,
          discount_type: values.discountType || null
        };
      }
    } else {
      // Fallback to stored config
      finalDiscountConfig = {
        ...discountConfig,
        discount: values.discountGiven || null,
        discount_type: values.discountType || null
      };
    }

    // Build the update payload with all fields matching the modal's structure
    // This ensures all data is sent to the API just like from the modal
    const updateValues = {
      phone_number: values.phone_number,
      about_me: values.about_me,
      instagram_handle: values.instagram_handle,
      twitter_handle: values.twitter_handle,
      tiktok_handle: values.tiktok_handle,
      facebook_handle: values.facebook_handle,
      latitude: values.latitude,
      longitude: values.longitude,
      address: values.address,
      street_number: values.street_number,
      city: values.city,
      state: values.state,
      zip_code: values.zip_code,
      profile_picture_id: values.profile_picture_id,
      store_email: values.store_email,
      rewardType: values.rewardType,
      demoCommission: values.demoCommission,
      commissionType: values.commissionType,
      salesCommission: values.salesCommission,
      salesCommissionType: values.salesCommissionType,
      no_show_demo_commission_type: values.no_show_demo_commission_type,
      no_show_demo_commission: values.no_show_demo_commission,
      discountType: values.discountType,
      discountGiven: values.discountGiven,
      paymentFrequency: values.payment_frequency || userProfile?.store?.payment_frequency,
      timeEligibility: values.timeEligibility,
      is_guest_discount_active: discountEnabled
    };

    // Always include discount_config in the update payload, regardless of enabled/disabled status
    updateValues.discount_config = finalDiscountConfig;

    const res = updateStoreUserProfile(updateValues);
    res
      .then(function (response) {
        if (response.status === 200) {
          setSubmitting(false);
          setIsEditing(false);
        }
      })
      .catch(function () {
        logger.error("[StoreUserProfile] updateStoreUserProfile failed");
        setSubmitting(false);
        setError(true);
      });
  };

  /**
   * Handles discount config changes from DiscountConfigForm.
   *
   * @param {Object} config - Transformed discount configuration
   * @returns {void}
   */
  const handleDiscountConfigChange = config => {
    setDiscountConfig(config);
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          phone_number: userProfile?.phone_number,
          about_me: userProfile?.about_me,
          instagram_handle: userProfile?.user_social?.instagram_handle,
          twitter_handle: userProfile?.user_social?.twitter_handle,
          tiktok_handle: userProfile?.user_social?.tiktok_handle,
          facebook_handle: userProfile?.user_social?.facebook_handle,
          latitude: userProfile?.user_geo_location?.latitude,
          longitude: userProfile?.user_geo_location?.longitude,
          address: userProfile?.user_geo_location?.address,
          street_number: userProfile?.user_geo_location?.street_number,
          city: userProfile?.user_geo_location?.city,
          state: userProfile?.user_geo_location?.state,
          zip_code: userProfile?.user_geo_location?.zip_code,
          profile_picture_id: userProfile?.profile_picture?.id,
          store_email: userProfile?.user_shopify[0]?.store_email,
          rewardType: initialRewardType,
          demoCommission: userProfile?.store?.demo_commission,
          commissionType: userProfile?.store?.demo_commission_type,
          salesCommission: userProfile?.store?.demo_sales_commission,
          salesCommissionType: userProfile?.store?.demo_sales_commission_type,
          no_show_demo_commission_type:
            userProfile?.store?.no_show_demo_commission_type,
          no_show_demo_commission: userProfile?.store?.no_show_demo_commission,
          discountType: userProfile?.store?.discount_type,
          discountGiven: userProfile?.store?.discount_given,
          payment_frequency: userProfile?.store?.payment_frequency,
          timeEligibility: userProfile?.store?.sales_commission_order_threshold,
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ errors, touched, values, setFieldValue }) => {
          const currentRewardType = normalizeRewardType(values?.rewardType);
          const allowedRewardTypes = userProfile?.store?.allowed_host_reward_types || [];

          /**
           * Gets the CSS className for the reward type label span based on selection state.
           *
           * @param {string} rewardType - The reward type value to check
           * @returns {string} "fw-bold" if the reward type is currently selected, "text-muted" otherwise
           */
          const getSpanClassName = rewardType => {
            if (currentRewardType === rewardType) {
              return 'fw-bold';
            }
            return 'text-muted';
          };

          /**
           * Renders reward type radio button options based on allowed reward types.
           * Filters the available reward types based on allowed_host_reward_types from the store.
           * If no allowed types are specified, shows all options for backward compatibility.
           *
           * @returns {Array<JSX.Element>} Array of label elements containing radio buttons for each allowed reward type
           */
          const renderRewardTypeOptions = () => {
            const rewardTypeConfig = [
              { type: HOST_REWARD_TYPES.CASH.VALUE, label: HOST_REWARD_TYPES.CASH.LABEL },
              { type: HOST_REWARD_TYPES.STORE_CREDIT.VALUE, label: HOST_REWARD_TYPES.STORE_CREDIT.LABEL },
              { type: HOST_REWARD_TYPES.GIFT_CARD.VALUE, label: HOST_REWARD_TYPES.GIFT_CARD.LABEL }
            ];

            const optionsToShow = [];

            // If no allowed types specified, show all (backward compatibility)
            if (!Array.isArray(allowedRewardTypes) || allowedRewardTypes.length === 0) {
              rewardTypeConfig.forEach(config => {
                optionsToShow.push(config);
              });
            } else {
              // Only show allowed types
              rewardTypeConfig.forEach(config => {
                if (allowedRewardTypes.includes(config.type)) {
                  optionsToShow.push(config);
                }
              });
            }

            return optionsToShow.map(config => {
              return (
                <label
                  key={config.type}
                  className="d-flex align-items-center"
                  style={{ cursor: 'pointer', fontSize: '1.1rem' }}
                >
                  <input
                    type="radio"
                    name="rewardType"
                    value={config.type}
                    checked={currentRewardType === config.type}
                    onChange={e => handleRewardTypeChange(e.target.value)}
                    disabled={rewardTypeLoading || rewardTypeSaving}
                    style={{ marginRight: '0.6rem', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span className={getSpanClassName(config.type)}>
                    {config.label}
                  </span>
                </label>
              );
            });
          };

          /**
           * Renders a loading spinner when reward type is being loaded or saved.
           *
           * @returns {JSX.Element|null} Spinner component if loading/saving, null otherwise
           */
          const renderLoadingSpinner = () => {
            if (rewardTypeLoading || rewardTypeSaving) {
              return (
                <Spinner
                  animation="border"
                  size="sm"
                  className="text-secondary"
                />
              );
            }
            return null;
          };

          /**
           * Renders an error message if there's an error with the reward type operation.
           *
           * @returns {JSX.Element|null} Error message div if error exists, null otherwise
           */
          const renderErrorMessage = () => {
            if (rewardTypeError) {
              return (
                <div className="text-danger small mt-2">
                  {rewardTypeError}
                </div>
              );
            }
            return null;
          };

          const handleRewardTypeChange = async (newType) => {
            if (newType === currentRewardType) {
              return;
            }

            setRewardTypeSaving(true);
            setRewardTypeError("");
            try {
              await updateStoreRewardType(newType);
              setFieldValue("rewardType", newType);
            } catch (toggleError) {
              logger.error("[StoreUserProfile] updateStoreRewardType failed");
              // Handle 404 - user might not have StoreAdmins record, but we can still update via profile
              if (toggleError?.response?.status === 404) {
                // Try to update via the profile update instead
                try {
                  const profileUpdateValues = {
                    ...values,
                    rewardType: newType,
                  };
                  await updateStoreUserProfile(profileUpdateValues);
                  setFieldValue("rewardType", newType);
                  setRewardTypeError("");
                } catch (profileError) {
                  logger.error(
                    "[StoreUserProfile] fallback reward update failed"
                  );
                  setRewardTypeError(
                    "Unable to update reward preference. Please try again."
                  );
                }
              } else {
                setRewardTypeError(
                  "Unable to update reward preference. Please try again."
                );
              }
            } finally {
              setRewardTypeSaving(false);
            }
          };

          return (
            <Form>
              <div className="row mb-4">
                <div className="col-sm-12 col-md-12 commission_setting_view_col">
                  <h6 className="mb-3">
                    <div className="commission_text">Real Customer Reward Type</div>
                    <InfoText text="Choose how real customers are rewarded: cash payouts, store credit, or gift cards." />
                  </h6>
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: "1.5rem" }}>
                    {renderRewardTypeOptions()}
                    {renderLoadingSpinner()}
                  </div>
                  {renderErrorMessage()}
                </div>
              </div>
              <hr />
              <div className="row ">
                <div className="mb-1 col-sm-12 col-md-12 commission_setting_view_col">
                  <h6 className="mb-0">Video meeting Commission</h6>
                </div>
                <div className="col col-sm-8 col-md-6 text-secondary">
                  <Field
                    placeholder="Video meeting commission (Eg. 12.99)"
                    type="text"
                    name="demoCommission"
                    className={`form-control form-control-sm`}
                  />
                </div>
                <div
                  className="col col-sm-4 col-md-2 text-secondary d-flex flex-column align-items-start"
                  role="group"
                >
                  <label className="mb-1 display-flex">
                    <Field type="radio" name="commissionType" value="AMOUNT" />{" "}
                    $
                  </label>
                </div>
              </div>
              <hr />
              <div className="row ">
                <div className="mb-1 col-sm-12 col-md-12 commission_setting_view_col">
                  <h6 className="mb-0">Sales Commission</h6>
                </div>
                <div className="col col-sm-8 col-md-6 text-secondary">
                  <Field
                    placeholder="Sales commission (Eg. 12.99)"
                    type="text"
                    name="salesCommission"
                    className={`form-control form-control-sm`}
                  />
                </div>
                <div
                  className="col col-sm-4 col-md-2 text-secondary d-flex flex-column align-items-start"
                  role="group"
                >
                  <label className="mb-1 display-flex">
                    <Field
                      type="radio"
                      name="salesCommissionType"
                      value="AMOUNT"
                    />
                    $
                  </label>
                  <label className="mb-1 display-flex">
                    <Field
                      type="radio"
                      name="salesCommissionType"
                      value="PERCENTAGE"
                    />
                    %
                  </label>
                </div>
              </div>
              <hr />
              <div className="row ">
                <div className="mb-1 col-sm-12 col-md-12 commission_setting_view_col">
                  <h6 className="mb-0">No Show Commission</h6>
                </div>
                <div className="col col-sm-8 col-md-6 text-secondary">
                  <Field
                    placeholder="No show commission (Eg. 12.99)"
                    type="text"
                    name="no_show_demo_commission"
                    className={`form-control form-control-sm`}
                  />
                </div>
                <div
                  className="col col-sm-4 col-md-2 text-secondary d-flex flex-column align-items-start"
                  role="group"
                >
                  <label className="mb-1 display-flex">
                    <Field
                      type="radio"
                      name="no_show_demo_commission_type"
                      value="AMOUNT"
                    />
                    $
                  </label>
                </div>
              </div>
              <hr />
              <div className="row ">
                <div className="col-sm-12 col-md-12 mb-1 commission_setting_view_col">
                  <h6 className="mb-0">Discount</h6>
                </div>
                <div className="col-sm-12 col-md-12 mb-2">
                  <label className="d-flex align-items-center" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={discountEnabled}
                      onChange={e => setDiscountEnabled(e.target.checked)}
                      style={{ marginRight: '0.5rem', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Enable Discount</span>
                  </label>
                </div>
                <div className="col col-sm-8 col-md-6 text-secondary">
                  <Field
                    placeholder="Discount (Eg. 12.99)"
                    type="text"
                    name="discountGiven"
                    className={`form-control form-control-sm`}
                    disabled={!discountEnabled}
                  />
                </div>
                <div
                  className="col col-sm-4 col-md-2 text-secondary d-flex flex-column align-items-start"
                  role="group"
                >
                  <label className="mb-1 display-flex">
                    <Field type="radio" name="discountType" value="AMOUNT" disabled={!discountEnabled} />$
                  </label>
                  <label className="mb-1 display-flex">
                    <Field
                      type="radio"
                      name="discountType"
                      value="PERCENTAGE"
                      disabled={!discountEnabled}
                    />
                    %
                  </label>
                </div>
                    <div className="col-sm-12 col-md-12 mt-3">
                      <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', opacity: discountEnabled ? 1 : 0.6 }}>
                        <DiscountConfigForm
                          ref={discountConfigFormRef}
                          discountConfig={discountConfig}
                          isOnboarding={false}
                          inline={true}
                          onChange={handleDiscountConfigChange}
                          disabled={!discountEnabled}
                        />
                      </div>
                    </div>
              </div>
              <hr />
              <div className="row ">
                <div className="col-sm-12 col-md-12 mb-1 commission_setting_view_col">
                  <h6 className="mb-0">Sales Commission Time Eligibility</h6>
                </div>
                <div className="col col-sm-8 col-md-6 text-secondary">
                  <Field
                    placeholder="Days (Eg. 30)"
                    type="number"
                    name="timeEligibility"
                    min="1"
                    className={`form-control form-control-sm`}
                  />
                  {errors?.timeEligibility && touched?.timeEligibility && (
                    <div
                      className="invalid-feedback"
                      style={{ display: "block" }}
                    >
                      {errors?.timeEligibility}
                    </div>
                  )}
                </div>
              </div>
              <hr />
              {error ? (
                <>
                  <div>
                    <p>Some error occured, check details</p>
                  </div>
                  <hr />
                </>
              ) : (
                <></>
              )}

              <div className="row">
                <div className="col-sm-3"></div>
                <div className="col-sm-9 text-secondary">
                  <button
                    className="add_email_button"
                    type="submit"
                    disabled={submitting}
                  >
                    Save Changes
                  </button>
                  <button
                    className="add_email_button"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};